import {
  useInfiniteQuery,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query";
import { useEffect, useMemo, useRef } from "react";
import { sortTopLevelCommentsDesc } from "@/features/study-group-stream/utils/streamUtil";
import { useUpdateStudyGroupLastVisit } from "@/hooks/useUpdateStudyGroupLastVisit";
import { commentsApi } from "@/lib/humhub-api/comments";
import type { EditableFileOps } from "@/components/common/hooks/useEditableAttachments";
import type {
  Comment,
  PaginatedResponse,
  ReactionType,
} from "@/lib/humhub-api/types";
import {
  getRetainedRestorationQueryOptions,
  markFavoritePostsCacheStale,
  postsKeys,
} from "@/lib/utils/queryCacheUtils";
import {
  applyReactionSummaryChange,
  createEmptyReactionSummary,
} from "@/lib/utils/reactionSummary";

const COMMENTS_PER_PAGE = 20;

// =====================
// Cache shapes
// =====================
interface CommentsQueryData {
  pages: PaginatedResponse<Comment>[]; // results: Comment[]
  pageParams: (number | undefined)[];
}

interface PostListQueryData {
  pages: {
    results: {
      metadata?: { object_id?: number };
      comments?: { total?: number };
    }[];
  }[];
}

interface OptimisticComment extends Comment {
  _optimistic?: boolean;
}

export interface UsePostCommentsOptions {
  postContentId: number;
  postObjectId: number;
  groupId?: number | null;
  currentUser?: {
    id: number;
    guid?: string;
    display_name: string;
    url?: string;
    image_url?: string;
  } | null;
  selectedTopics?: Array<{
    id?: number | string;
    name: string;
    topic_kind?: string;
  }>;
  initialComments?: Comment[];
  initialTotal?: number;
  enabled?: boolean;
  isRetainedRestoration?: boolean;
  mergeInitialCommentsIntoRetainedCache?: boolean;
}

// API側のファイル情報（コメントに付くファイルの型の最小要件）
type ApiFileLite = {
  id: number;
  guid: string;
  mime_type: string;
  size: string | number;
  file_name: string;
  url: string;
  thumbnail_url?: string;
  preview_url?: string | null;
  download_url?: string | null;
  access_url_expires_at?: number | null;
};

// =====================
// Mutation contexts
// =====================
type CommentsContext = { prev?: CommentsQueryData };
type CommentsAndListContext = {
  prevComments?: CommentsQueryData;
  prevList?: PostListQueryData;
  tmpUrls?: string[]; // createObjectURL の一時URL
};
type ReplyContext = {
  prev?: CommentsQueryData;
  tmpUrls?: string[];
  tempReplyId?: number;
};

const mapCommentsPages = (
  old: CommentsQueryData | undefined,
  mapResults: (comments: Comment[]) => Comment[],
): CommentsQueryData | undefined => {
  if (!old?.pages) return old;

  return {
    ...old,
    pages: old.pages.map((page) => ({
      ...page,
      results: mapResults(page.results ?? []),
    })),
  };
};

const mapCommentsRecursively = (
  comments: Comment[],
  mapComment: (comment: Comment) => Comment,
): Comment[] =>
  comments.map((comment) => {
    const nextComment = mapComment(comment);
    const nextReplies = mapCommentsRecursively(
      nextComment.comments ?? [],
      mapComment,
    );
    return {
      ...nextComment,
      comments: nextReplies,
    };
  });

const removeCommentRecursively = (
  comments: Comment[],
  commentId: number,
): Comment[] =>
  comments
    .filter((comment) => Number(comment.id) !== Number(commentId))
    .map((comment) => ({
      ...comment,
      comments: removeCommentRecursively(comment.comments ?? [], commentId),
    }));

// =====================
// ダミーの空ページ（型整合性を満たす links, results を付与）
// =====================
const createEmptyPage = (): PaginatedResponse<Comment> => ({
  results: [] as Comment[],
  page: 1,
  pages: 1,
  total: 0,
  links: { self: "", first: "", last: "" }, // 必須キーを満たす
});

const createInitialCommentsPage = (
  comments: Comment[],
  total: number,
): PaginatedResponse<Comment> => ({
  results: comments,
  page: 1,
  pages: Math.max(1, Math.ceil(total / COMMENTS_PER_PAGE)),
  total,
  links: { self: "", first: "", last: "" },
});

const mergeCommentTrees = (
  current: Comment[],
  incoming: Comment[],
): Comment[] => {
  const currentById = new Map(
    current.map((comment) => [Number(comment.id), comment]),
  );
  const incomingIds = new Set(incoming.map((comment) => Number(comment.id)));
  return [
    ...incoming.map((comment) => {
      const retained = currentById.get(Number(comment.id));
      if (!retained) return comment;
      return {
        ...retained,
        ...comment,
        comments: mergeCommentTrees(
          retained.comments ?? [],
          comment.comments ?? [],
        ),
      };
    }),
    ...current.filter((comment) => !incomingIds.has(Number(comment.id))),
  ];
};

const mergeInitialCommentsIntoPages = (
  current: CommentsQueryData,
  incoming: Comment[],
  incomingTotal: number,
): CommentsQueryData => {
  const incomingById = new Map(
    incoming.map((comment) => [Number(comment.id), comment]),
  );
  const retainedIds = new Set(
    current.pages.flatMap((page) =>
      page.results.map((comment) => Number(comment.id)),
    ),
  );
  const newComments = incoming.filter(
    (comment) => !retainedIds.has(Number(comment.id)),
  );
  const pages = current.pages.map((page, pageIndex) => {
    const pageIncoming = page.results.flatMap((comment) => {
      const match = incomingById.get(Number(comment.id));
      return match ? [match] : [];
    });
    const results = mergeCommentTrees(page.results, pageIncoming);
    if (pageIndex === 0 && newComments.length > 0) {
      results.unshift(...newComments);
    }
    return {
      ...page,
      results,
      total: Math.max(page.total ?? 0, incomingTotal),
    };
  });
  return { ...current, pages };
};

// =====================
// File[] -> ApiFileLite[] 正規化 + 一時URL収集
// =====================
const mapFilesToApiWithUrls = (files?: File[]) => {
  const tmpUrls: string[] = [];
  const mapped: ApiFileLite[] = (files ?? []).map((f, idx) => {
    const url = URL.createObjectURL(f);
    tmpUrls.push(url);
    return {
      id: -Date.now() - idx, // 一時ID
      guid: `temp-${Date.now()}-${idx}`,
      mime_type: f.type || "application/octet-stream",
      size: f.size,
      file_name: f.name,
      url, // プレビュー用
    };
  });
  return { mappedFiles: mapped, tmpUrls };
};

// =====================
// 一時URLの解放（forEachではなくfor...ofでLint回避）
// =====================
const revokeObjectUrls = (urls?: string[]) => {
  if (!urls || urls.length === 0) return;
  for (const u of urls) {
    URL.revokeObjectURL(u);
  }
};

// =====================
// 型ガード群（no-any）
// =====================
const isRecord = (v: unknown): v is Record<string, unknown> =>
  typeof v === "object" && v !== null;

const isComment = (v: unknown): v is Comment =>
  isRecord(v) && "id" in v && "message" in v;

const isCommentArray = (v: unknown): v is Comment[] =>
  Array.isArray(v) && v.every(isComment);

const isComment2DArray = (v: unknown): v is Comment[][] =>
  Array.isArray(v) && v.every(isCommentArray);

type CommentsApiResponse = Omit<PaginatedResponse<Comment>, "results"> & {
  results: unknown;
};

const isCommentsApiResponse = (x: unknown): x is CommentsApiResponse => {
  if (!isRecord(x)) return false;
  const links = x.links;
  return (
    "results" in x &&
    isRecord(links) &&
    typeof links.self === "string" &&
    typeof links.first === "string" &&
    typeof links.last === "string"
  );
};

// =====================
// API戻りを unknown から安全に正規化（no-any / 厳密絞り込み）
// =====================
const fetchCommentsNormalized = async (
  contentId: number,
  page: number,
): Promise<PaginatedResponse<Comment>> => {
  const resUnknown = await commentsApi.getComments(contentId, page);
  if (!isCommentsApiResponse(resUnknown)) {
    // 想定外の形：空で返す（必要なら throw に変更可）
    return createEmptyPage();
  }

  const raw = resUnknown.results;
  let results: Comment[] = [];

  if (isComment2DArray(raw)) {
    results = raw.flat(); // Comment[] に確定
  } else if (isCommentArray(raw)) {
    results = raw; // Comment[] に確定
  } else {
    results = []; // どちらでもなければ空
  }

  const normalized: PaginatedResponse<Comment> = {
    ...resUnknown,
    results,
  };
  return normalized;
};

export function usePostComments({
  postContentId,
  postObjectId,
  groupId, // 任意: ポスト一覧の total 同期に使う
  currentUser, // 楽観コメント用
  selectedTopics,
  initialComments,
  initialTotal,
  enabled = true,
  isRetainedRestoration = false,
  mergeInitialCommentsIntoRetainedCache = false,
}: UsePostCommentsOptions) {
  const queryClient = useQueryClient();
  const commentsKey = useMemo(
    () => postsKeys.comments(Number(postContentId)),
    [postContentId],
  );
  const listKey = useMemo(
    () =>
      typeof groupId === "number" && groupId > 0
        ? postsKeys.list(groupId)
        : null,
    [groupId],
  );
  const { mutateAsync: updateLastVisit } = useUpdateStudyGroupLastVisit(
    groupId ?? null,
  );
  const lastVisitDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );
  const lastInitialCommentsSyncSignatureRef = useRef<string | null>(null);
  const LAST_VISIT_DEBOUNCE_MS = 500;
  const initialCommentsSyncSignature = useMemo(() => {
    if (initialComments === undefined) return null;
    return JSON.stringify({
      total: initialTotal ?? initialComments.length,
      comments: initialComments,
    });
  }, [initialComments, initialTotal]);

  const handleLastVisitUpdate = (action: string) => {
    if (!groupId) return;
    if (lastVisitDebounceRef.current) {
      clearTimeout(lastVisitDebounceRef.current);
    }
    lastVisitDebounceRef.current = setTimeout(async () => {
      lastVisitDebounceRef.current = null;
      try {
        await updateLastVisit();
      } catch (err) {
        console.warn(`Failed to update last visit after ${action}`, err);
      }
    }, LAST_VISIT_DEBOUNCE_MS);
  };

  useEffect(() => {
    return () => {
      if (lastVisitDebounceRef.current) {
        clearTimeout(lastVisitDebounceRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (enabled || initialComments === undefined) return;
    if (
      lastInitialCommentsSyncSignatureRef.current ===
      initialCommentsSyncSignature
    ) {
      return;
    }
    lastInitialCommentsSyncSignatureRef.current = initialCommentsSyncSignature;
    if (isRetainedRestoration && queryClient.getQueryData(commentsKey)) {
      if (mergeInitialCommentsIntoRetainedCache) {
        queryClient.setQueryData<CommentsQueryData>(commentsKey, (current) =>
          current
            ? mergeInitialCommentsIntoPages(
                current,
                initialComments,
                initialTotal ?? initialComments.length,
              )
            : current,
        );
      }
      return;
    }

    queryClient.setQueryData<CommentsQueryData>(commentsKey, {
      pages: [
        createInitialCommentsPage(
          initialComments,
          initialTotal ?? initialComments.length,
        ),
      ],
      pageParams: [1],
    });
  }, [
    commentsKey,
    enabled,
    initialComments,
    initialCommentsSyncSignature,
    initialTotal,
    isRetainedRestoration,
    mergeInitialCommentsIntoRetainedCache,
    queryClient,
  ]);

  // =====================
  // Query: コメント一覧 (Infinite)
  // =====================
  const {
    data,
    isLoading,
    isError,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isRefetching,
    refetch,
  } = useInfiniteQuery<PaginatedResponse<Comment>>({
    queryKey: commentsKey,
    queryFn: async (context) => {
      const page = (context.pageParam as number) ?? 1;
      return await fetchCommentsNormalized(Number(postContentId), page);
    },
    getNextPageParam: (lastPage): number | undefined => {
      try {
        if (lastPage?.links?.next) {
          const urlStr = lastPage.links.next;
          const qs = urlStr.startsWith("http")
            ? new URL(urlStr).searchParams
            : new URLSearchParams(urlStr.split("?")[1] || "");
          const page = qs.get("page");
          return page ? parseInt(page, 10) : undefined;
        }
        if (lastPage?.page && lastPage?.pages) {
          const next = (lastPage.page || 1) + 1;
          return next <= lastPage.pages ? next : undefined;
        }
      } catch {
        // ignore
      }
      return undefined;
    },
    initialPageParam: 1,
    initialData:
      initialComments !== undefined
        ? {
            pages: [
              createInitialCommentsPage(
                initialComments,
                initialTotal ?? initialComments.length,
              ),
            ],
            pageParams: [1],
          }
        : undefined,
    initialDataUpdatedAt: initialComments !== undefined ? 0 : undefined,
    enabled,
    staleTime: 5 * 60 * 1000,
    ...getRetainedRestorationQueryOptions(isRetainedRestoration),
    refetchOnWindowFocus: false,
  });

  // フラット化 & トップレベルの子コメントを desc ソート
  const comments = useMemo(() => {
    const flat: Comment[] = [];
    data?.pages?.forEach((page) => {
      if (page?.results) flat.push(...page.results);
    });
    return flat.map((c) => ({
      ...c,
      comments: sortTopLevelCommentsDesc([...(c.comments ?? [])]),
    }));
  }, [data]);

  const total = useMemo(() => {
    const fromApi = data?.pages?.[0]?.total;
    return typeof fromApi === "number" ? fromApi : comments.length;
  }, [data, comments.length]);

  // =====================
  // 追加（トップレベル）
  // =====================
  const addComment = useMutation<
    Comment,
    unknown,
    {
      content: string;
      files?: File[];
      topics?: Array<{
        id?: number | string;
        name: string;
        topic_kind?: string;
      }>;
    },
    CommentsAndListContext
  >({
    mutationFn: async ({ content, files, topics }) =>
      commentsApi.createComment(
        Number(postObjectId),
        content,
        files,
        topics ?? selectedTopics,
      ),
    onMutate: async ({ content, files }) => {
      await queryClient.cancelQueries({ queryKey: commentsKey });
      if (listKey) await queryClient.cancelQueries({ queryKey: listKey });

      const prevComments =
        queryClient.getQueryData<CommentsQueryData>(commentsKey);
      const prevList = listKey
        ? queryClient.getQueryData<PostListQueryData>(listKey)
        : undefined;

      const { mappedFiles, tmpUrls } = mapFilesToApiWithUrls(files);

      const optimistic: OptimisticComment = {
        id: Date.now(),
        message: content,
        user: {
          id: currentUser?.id ?? 0,
          guid: currentUser?.guid ?? "",
          display_name: currentUser?.display_name ?? "You",
          url: currentUser?.url ?? "",
          image_url: currentUser?.image_url ?? "",
        },
        created_at: new Date().toISOString(),
        isSending: true,
        reactions: createEmptyReactionSummary(),
        files: mappedFiles, // ← File[] ではなく API 風の型に
        comments: [],
        _optimistic: true,
      };

      queryClient.setQueryData<CommentsQueryData>(commentsKey, (old) => {
        const pages = old?.pages?.length ? old.pages : [createEmptyPage()];
        const first = pages[0];

        const merged = sortTopLevelCommentsDesc([
          optimistic,
          ...(first.results || []),
        ]);
        const newFirst: PaginatedResponse<Comment> = {
          ...first,
          results: merged,
          total:
            typeof first.total === "number"
              ? Math.max(first.total + 1, merged.length)
              : merged.length,
        };

        return {
          pages: [newFirst, ...pages.slice(1)],
          pageParams: old?.pageParams ?? [1],
        };
      });

      // 一覧 total +1（対象postは objectId で同定）
      if (listKey) {
        queryClient.setQueryData<PostListQueryData>(listKey, (old) => {
          if (!old?.pages) return old;
          return {
            ...old,
            pages: old.pages.map((page) => ({
              ...page,
              results: page.results.map((post) =>
                Number(post?.metadata?.object_id) === Number(postObjectId)
                  ? {
                      ...post,
                      comments: {
                        ...(post.comments || {}),
                        total: (post.comments?.total ?? 0) + 1,
                      },
                    }
                  : post,
              ),
            })),
          };
        });
      }

      return { prevComments, prevList, tmpUrls };
    },
    onError: (_e, _v, ctx) => {
      console.log("[usePostComments] addComment failed", {
        content: _v.content,
        hasFiles: Boolean(_v.files?.length),
        error: _e,
      });
      if (ctx?.prevComments)
        queryClient.setQueryData(commentsKey, ctx.prevComments);
      if (listKey && ctx?.prevList)
        queryClient.setQueryData(listKey, ctx.prevList);
    },
    onSuccess: () => {
      handleLastVisitUpdate("adding comment");
    },
    onSettled: (_d, _e, _v, ctx) => {
      revokeObjectUrls(ctx?.tmpUrls); // ← forEach を避ける
      queryClient.invalidateQueries({ queryKey: commentsKey });
      if (listKey) queryClient.invalidateQueries({ queryKey: listKey });
      markFavoritePostsCacheStale(queryClient);
    },
  });

  // =====================
  // 返信
  // =====================
  const addReply = useMutation<
    Comment,
    unknown,
    {
      parentCommentId: number;
      content: string;
      files?: File[];
      topics?: Array<{
        id?: number | string;
        name: string;
        topic_kind?: string;
      }>;
    },
    ReplyContext
  >({
    mutationFn: async ({ parentCommentId, content, files, topics }) =>
      commentsApi.createCommentReply(
        parentCommentId,
        content,
        files,
        topics ?? selectedTopics,
      ),
    onMutate: async ({ parentCommentId, content, files }) => {
      await queryClient.cancelQueries({ queryKey: commentsKey });
      const prev = queryClient.getQueryData<CommentsQueryData>(commentsKey);
      const tempReplyId = -Date.now();

      const { mappedFiles, tmpUrls } = mapFilesToApiWithUrls(files);

      const optimistic: OptimisticComment = {
        id: tempReplyId,
        message: content,
        user: {
          id: currentUser?.id ?? 0,
          guid: currentUser?.guid ?? "",
          display_name: currentUser?.display_name ?? "You",
          url: currentUser?.url ?? "",
          image_url: currentUser?.image_url ?? "",
        },
        created_at: new Date().toISOString(),
        isSending: true,
        reactions: createEmptyReactionSummary(),
        files: mappedFiles,
        comments: [],
        _optimistic: true,
      };

      queryClient.setQueryData<CommentsQueryData>(commentsKey, (old) => {
        return mapCommentsPages(old, (results) =>
          results.map((comment) =>
            Number(comment.id) === Number(parentCommentId)
              ? {
                  ...comment,
                  comments: sortTopLevelCommentsDesc([
                    ...(comment.comments || []),
                    optimistic,
                  ]),
                }
              : comment,
          ),
        );
      });

      return { prev, tmpUrls, tempReplyId };
    },
    onError: (_e, _v, ctx) => {
      if (ctx?.prev) queryClient.setQueryData(commentsKey, ctx.prev);
    },
    onSuccess: (createdReply, variables, ctx) => {
      const normalizedReply: Comment = {
        ...createdReply,
        isSending: false,
        isPending: false,
      };

      queryClient.setQueryData<CommentsQueryData>(commentsKey, (old) => {
        return mapCommentsPages(old, (results) =>
          results.map((comment) => {
            if (Number(comment.id) !== Number(variables.parentCommentId)) {
              return comment;
            }

            const existingReplies = comment.comments || [];
            let replaced = false;
            const nextReplies = existingReplies.map((reply) => {
              if (
                ctx?.tempReplyId !== undefined &&
                Number(reply.id) === Number(ctx.tempReplyId)
              ) {
                replaced = true;
                return normalizedReply;
              }
              return reply;
            });

            const mergedReplies = replaced
              ? nextReplies
              : [normalizedReply, ...nextReplies];

            return {
              ...comment,
              comments: sortTopLevelCommentsDesc(mergedReplies),
            };
          }),
        );
      });
      handleLastVisitUpdate("replying to comment");
    },
    onSettled: (_d, _e, _v, ctx) => {
      revokeObjectUrls(ctx?.tmpUrls); // ← forEach を避ける
      queryClient.invalidateQueries({ queryKey: commentsKey });
      markFavoritePostsCacheStale(queryClient);
    },
  });

  // =====================
  // 編集
  // =====================
  const editComment = useMutation<
    Comment,
    unknown,
    { commentId: number; newMessage: string; fileOps?: EditableFileOps },
    CommentsContext
  >({
    mutationFn: async ({ commentId, newMessage, fileOps }) => {
      await Promise.all([
        ...(fileOps?.filesToRemove ?? []).map((fileId) =>
          commentsApi.removeCommentFile(commentId, fileId),
        ),
        ...(fileOps?.filesToReplace ?? []).map(({ fileId, newFile }) =>
          commentsApi.replaceCommentFile(commentId, fileId, newFile),
        ),
        ...(fileOps?.filesToAdd.length
          ? [commentsApi.uploadFilesToComment(commentId, fileOps.filesToAdd)]
          : []),
      ]);
      return commentsApi.editComment(commentId, newMessage);
    },
    onMutate: async ({ commentId, newMessage }) => {
      await queryClient.cancelQueries({ queryKey: commentsKey });
      const prev = queryClient.getQueryData<CommentsQueryData>(commentsKey);

      queryClient.setQueryData<CommentsQueryData>(commentsKey, (old) =>
        mapCommentsPages(old, (results) =>
          mapCommentsRecursively(results, (comment) =>
            Number(comment.id) === Number(commentId)
              ? { ...comment, message: newMessage }
              : comment,
          ),
        ),
      );
      return { prev };
    },
    onError: (_e, _v, ctx) => {
      if (ctx?.prev) queryClient.setQueryData(commentsKey, ctx.prev);
    },
    onSuccess: (updatedComment) => {
      queryClient.setQueryData<CommentsQueryData>(commentsKey, (old) =>
        mapCommentsPages(old, (results) =>
          mapCommentsRecursively(results, (comment) =>
            Number(comment.id) === Number(updatedComment.id)
              ? {
                  ...comment,
                  ...updatedComment,
                  comments: updatedComment.comments ?? comment.comments,
                }
              : comment,
          ),
        ),
      );
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: commentsKey });
      markFavoritePostsCacheStale(queryClient);
    },
  });

  // =====================
  // 削除
  // =====================
  const deleteComment = useMutation<
    unknown,
    unknown,
    { commentId: number },
    CommentsAndListContext
  >({
    mutationFn: async ({ commentId }) => commentsApi.deleteComment(commentId),
    onMutate: async ({ commentId }) => {
      await queryClient.cancelQueries({ queryKey: commentsKey });
      const prevComments =
        queryClient.getQueryData<CommentsQueryData>(commentsKey);
      const prevList = listKey
        ? queryClient.getQueryData<PostListQueryData>(listKey)
        : undefined;

      queryClient.setQueryData<CommentsQueryData>(commentsKey, (old) =>
        mapCommentsPages(old, (results) =>
          removeCommentRecursively(results, commentId),
        ),
      );

      // 一覧 total -1（objectId で該当ポストを特定）
      if (listKey) {
        queryClient.setQueryData<PostListQueryData>(listKey, (old) => {
          if (!old?.pages) return old;
          return {
            ...old,
            pages: old.pages.map((page) => ({
              ...page,
              results: page.results.map((post) =>
                Number(post?.metadata?.object_id) === Number(postObjectId)
                  ? {
                      ...post,
                      comments: {
                        ...(post.comments || {}),
                        total: Math.max(0, (post.comments?.total ?? 1) - 1),
                      },
                    }
                  : post,
              ),
            })),
          };
        });
      }

      return { prevComments, prevList };
    },
    onError: (_e, _v, ctx) => {
      if (ctx?.prevComments)
        queryClient.setQueryData(commentsKey, ctx.prevComments);
      if (listKey && ctx?.prevList)
        queryClient.setQueryData(listKey, ctx.prevList);
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: commentsKey });
      if (listKey) queryClient.invalidateQueries({ queryKey: listKey });
      markFavoritePostsCacheStale(queryClient);
    },
  });

  // =====================
  // リアクション
  // =====================
  const reactComment = useMutation<
    unknown,
    unknown,
    { commentId: number; reaction: ReactionType | null },
    CommentsContext
  >({
    mutationFn: async ({ commentId, reaction }) =>
      commentsApi.createOrDeleteReaction(commentId, reaction, "comment"),
    onMutate: async ({ commentId, reaction }) => {
      await queryClient.cancelQueries({ queryKey: commentsKey });
      const prev = queryClient.getQueryData<CommentsQueryData>(commentsKey);

      const bump = (comment: Comment): Comment => {
        if (Number(comment.id) === Number(commentId)) {
          return {
            ...comment,
            reactions: applyReactionSummaryChange(comment.reactions, reaction),
          };
        }
        return comment;
      };

      queryClient.setQueryData<CommentsQueryData>(commentsKey, (old) =>
        mapCommentsPages(old, (results) =>
          mapCommentsRecursively(results, bump),
        ),
      );

      return { prev };
    },
    onError: (_e, _v, ctx) => {
      if (ctx?.prev) queryClient.setQueryData(commentsKey, ctx.prev);
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: commentsKey });
      markFavoritePostsCacheStale(queryClient);
    },
  });

  return {
    data,
    comments,
    total,
    isLoading,
    isError,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isRefetching,
    refetch,
    addComment,
    addReply,
    editComment,
    deleteComment,
    reactComment,
  };
}
